import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import {
  AttendeeStatus,
  EventType,
  PaymentMethod,
  PaymentStatus,
  PinLadder,
  TravelMode,
  TshirtSize,
} from "@satyrsmc/shared/lib/enums";
import type { EventAttendeeAdmin } from "@satyrsmc/shared/dto/admin/event";
import { includesTshirt, travelModesFor } from "@satyrsmc/shared/lib/day-pass-policy";
import { formatDateTime, formatWeekdayMonthDay, toDateOnly } from "@/lib/date-utils";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const TRAVEL_LABELS: Record<TravelMode, string> = {
  [TravelMode.Motorcycle]: "Motorcycle",
  [TravelMode.CarTruck]: "Car/Truck",
  [TravelMode.RvCamper]: "RV/Camper",
};

export type AttendeeFormState = {
  status: AttendeeStatus;
  waiver_signed: boolean;
  payment_status: PaymentStatus;
  payment_method: PaymentMethod | "";
  payment_amount: string;
  is_rider: boolean;
  dietary_restrictions: string;
  tshirt_size: TshirtSize | "";
  traveling_by: TravelMode | "";
  club: string;
  /** A day of the event for a day pass; "" means attending the full event. */
  attendance_date: string;
  checked_in: boolean;
  /** Read-only display value populated from the server; not itself submitted. */
  check_in_at: string | null;
  notes: string;
  pin_header: boolean;
  pin_ladder: PinLadder | null;
};

export function defaultFormState(): AttendeeFormState {
  return {
    status: AttendeeStatus.Yes,
    waiver_signed: false,
    payment_status: PaymentStatus.NotRequired,
    payment_method: "",
    payment_amount: "",
    is_rider: false,
    dietary_restrictions: "",
    tshirt_size: "",
    traveling_by: "",
    club: "",
    attendance_date: "",
    checked_in: false,
    check_in_at: null,
    notes: "",
    pin_header: false,
    pin_ladder: null,
  };
}

/** Best-effort Pin Ladder guess from travel mode, used when nothing has been
 * explicitly saved yet: motorcycle riders get the Biker ladder, everyone else
 * traveling by a known mode gets the Rider ladder. */
export function guessPinLadder(travelingBy: TravelMode | null | undefined): PinLadder | null {
  if (travelingBy == null) return null;
  return travelingBy === TravelMode.Motorcycle ? PinLadder.Biker : PinLadder.Rider;
}

export function AttendeeFormFields({
  form,
  set,
  eventType,
  dayPassDays,
}: {
  form: AttendeeFormState;
  set: <K extends keyof AttendeeFormState>(key: K, val: AttendeeFormState[K]) => void;
  eventType: EventType | null;
  /** Days this event can be registered for one at a time; empty when it cannot. */
  dayPassDays: string[];
}) {
  const isCampout = eventType === EventType.Badger || eventType === EventType.PioneerRun;
  const isRides = eventType === EventType.Rides;
  const needsPayment = form.payment_status !== PaymentStatus.NotRequired;
  const isDayPass = form.attendance_date !== "";

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Status</label>
          <Select value={form.status} onValueChange={(v) => set("status", v as AttendeeStatus)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AttendeeStatus.Yes}>Confirmed</SelectItem>
              <SelectItem value={AttendeeStatus.No}>Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2 pb-1">
          <label
            className={`flex items-center gap-1.5 text-xs cursor-pointer ${
              form.waiver_signed ? "" : "font-medium text-destructive"
            }`}
          >
            <input
              type="checkbox"
              checked={form.waiver_signed}
              onChange={(e) => set("waiver_signed", e.target.checked)}
              className="rounded"
            />
            Waiver signed
            {!form.waiver_signed && <AlertTriangle className="size-3.5" />}
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Payment status</label>
          <Select
            value={form.payment_status}
            onValueChange={(v) => set("payment_status", v as PaymentStatus)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PaymentStatus.NotRequired}>Not required</SelectItem>
              <SelectItem value={PaymentStatus.Pending}>Pending</SelectItem>
              <SelectItem value={PaymentStatus.Confirmed}>Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {needsPayment && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Payment method</label>
            <Select
              value={form.payment_method}
              onValueChange={(v) => set("payment_method", v as PaymentMethod)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PaymentMethod.Check}>Check</SelectItem>
                <SelectItem value={PaymentMethod.Cash}>Cash</SelectItem>
                <SelectItem value={PaymentMethod.Zelle}>Zelle</SelectItem>
                <SelectItem value={PaymentMethod.CreditCard}>Credit Card</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {needsPayment && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {form.payment_status === PaymentStatus.Confirmed
                ? "Amount paid ($)"
                : form.payment_status === PaymentStatus.Pending
                  ? "Amount owed ($)"
                  : "Amount ($)"}
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={form.payment_amount}
              onChange={(e) => set("payment_amount", e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        )}
      </div>

      {isRides && (
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_rider}
            onChange={(e) => set("is_rider", e.target.checked)}
            className="rounded"
          />
          Is rider
        </label>
      )}

      {isCampout && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-muted-foreground">Details</p>

          {/* Only the event's own days are offered, so an out-of-range date is unreachable. */}
          {dayPassDays.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Attending</label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: "Full event", value: "" },
                  ...dayPassDays.map((d) => ({
                    label: formatWeekdayMonthDay(d),
                    value: d,
                  })),
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    type="button"
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                      form.attendance_date === value
                        ? "border-primary bg-primary/10 font-medium"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => set("attendance_date", value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {/* A day-pass holder is not staying overnight, so no t-shirt and no RV. */}
            {includesTshirt(isDayPass) && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">T-shirt size</label>
                <Select
                  value={form.tshirt_size}
                  onValueChange={(v) => set("tshirt_size", v as TshirtSize)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(TshirtSize).map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Traveling by</label>
              <Select
                value={form.traveling_by}
                onValueChange={(v) => set("traveling_by", v as TravelMode)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {travelModesFor(isDayPass).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {TRAVEL_LABELS[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Club</label>
              <Input
                placeholder="Club name (optional)"
                value={form.club}
                onChange={(e) => set("club", e.target.value)}
                className="h-8 text-xs"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Dietary restrictions (optional)
            </label>
            <Input
              placeholder="Any dietary restrictions..."
              value={form.dietary_restrictions}
              onChange={(e) => set("dietary_restrictions", e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>
      )}

      <div className="space-y-3 rounded-lg border border-border p-3">
        <p className="text-xs font-medium text-muted-foreground">Check-In</p>

        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={form.checked_in}
            onChange={(e) => set("checked_in", e.target.checked)}
            className="rounded"
          />
          Checked in
          {form.checked_in && form.check_in_at && (
            <span className="text-muted-foreground">({formatDateTime(form.check_in_at)})</span>
          )}
        </label>

        {isCampout && (
          <>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={form.pin_header}
                onChange={(e) => set("pin_header", e.target.checked)}
                className="rounded"
              />
              Pin header
            </label>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Pin ladder</label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: "Biker", value: PinLadder.Biker },
                  { label: "Rider", value: PinLadder.Rider },
                  { label: "None", value: PinLadder.None },
                ].map(({ label, value }) => (
                  <button
                    key={value}
                    type="button"
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                      form.pin_ladder === value
                        ? "border-primary bg-primary/10 font-medium"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => set("pin_ladder", value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Notes</label>
          <Textarea
            placeholder="Optional notes..."
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            className="text-xs"
          />
        </div>
      </div>
    </>
  );
}

export function EditAttendeeDialog({
  attendee,
  eventId,
  eventType,
  dayPassDays,
  open,
  onOpenChange,
  onSaved,
}: {
  attendee: EventAttendeeAdmin;
  eventId: string;
  eventType: EventType | null;
  dayPassDays: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const isCampout = eventType === EventType.Badger || eventType === EventType.PioneerRun;
  const isRides = eventType === EventType.Rides;

  const formFromAttendee = (): AttendeeFormState => ({
    status: attendee.status,
    waiver_signed: attendee.waiver_signed,
    payment_status: attendee.payment_status ?? PaymentStatus.NotRequired,
    payment_method: attendee.payment_method ?? "",
    payment_amount:
      attendee.payment_amount_cents != null ? (attendee.payment_amount_cents / 100).toFixed(2) : "",
    is_rider: false,
    dietary_restrictions: attendee.dietary_restrictions ?? "",
    tshirt_size: attendee.campout_details?.tshirtSize ?? "",
    traveling_by: attendee.campout_details?.travelingBy ?? "",
    club: attendee.campout_details?.club ?? "",
    attendance_date: toDateOnly(attendee.attendance_date),
    checked_in: attendee.check_in_at != null,
    check_in_at: attendee.check_in_at,
    notes: attendee.notes ?? "",
    pin_header: attendee.campout_details?.pinHeader ?? false,
    pin_ladder:
      attendee.campout_details?.pinLadder ?? guessPinLadder(attendee.campout_details?.travelingBy),
  });

  const [form, setForm] = useState<AttendeeFormState>(formFromAttendee);

  useEffect(() => {
    if (open) setForm(formFromAttendee());
  }, [open]); // formFromAttendee intentionally reads attendee snapshot at open time

  const set = <K extends keyof AttendeeFormState>(key: K, val: AttendeeFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const updateMutation = trpc.admin.events.updateAttendee.useMutation({
    onSuccess: () => {
      toast.success("Attendee updated.");
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update attendee.");
    },
  });

  const handleSave = () => {
    const needsPayment = form.payment_status !== PaymentStatus.NotRequired;
    const amountCents =
      needsPayment && form.payment_amount
        ? Math.round(parseFloat(form.payment_amount) * 100)
        : null;

    updateMutation.mutate({
      eventId,
      attendeeId: attendee.id,
      status: form.status,
      waiver_signed: form.waiver_signed,
      payment_status: form.payment_status,
      payment_method: form.payment_method ? (form.payment_method as PaymentMethod) : null,
      payment_amount_cents: amountCents,
      is_rider: isRides ? form.is_rider : null,
      dietary_restrictions: form.dietary_restrictions || null,
      campout_details: isCampout
        ? {
            tshirtSize: form.tshirt_size ? (form.tshirt_size as TshirtSize) : null,
            travelingBy: form.traveling_by ? (form.traveling_by as TravelMode) : null,
            club: form.club || null,
            pinHeader: form.pin_header,
            pinLadder: form.pin_ladder,
          }
        : null,
      attendance_date: form.attendance_date || null,
      checked_in: form.checked_in,
      notes: form.notes || null,
    });
  };

  const name = attendee.member?.name ?? attendee.contact?.display_name ?? "Attendee";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Attendee — {name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <AttendeeFormFields
            form={form}
            set={set}
            eventType={eventType}
            dayPassDays={dayPassDays}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
